import { Collapse, Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Panel } from '../../Primitivas/Panel.tsx';
import {
    formatDistanceToNow,
    formatDistanceToNowStrict,
    fromUnixTime,
} from 'date-fns';
import { formatDateTime } from '../../Utils/Dates.ts';
import cookie from 'cookie';
import { JsonViewer } from '../Utils/JsonViewer.tsx';
import { useLazyQuery } from '@apollo/client';
import { TodayDocument } from '../../gql/graphql.ts';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { useEffect, useMemo, useState } from 'react';
import { useInterval } from 'usehooks-ts';

interface JWTFields {
    iat?: number;
    exp?: number;
    sub?: string;
    sessionHandle?: string;
    refreshTokenHash1?: string;
    parentRefreshTokenHash1?: string;
    antiCsrfToken?: string | null;
    iss?: string;
    legacy_id?: string;
    is_sadmin?: boolean;
}

interface TimingInfo {
    id: string;
    name: string;
    timestamp: number;
    date: string;
    diff: string;
}

export function DebugJwt() {
    const [jwtAbierto, jwtDisclosure] = useDisclosure(true);
    const [last_updated, set_last_updated] = useState(new Date());
    const [last_updated_text, set_last_updated_text] = useState('');
    const [jwt, set_jwt] = useState<JWTFields | undefined>(undefined);
    const [todayQuery, { error, data }] = useLazyQuery(TodayDocument, {
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });
    useInterval(
        () => {
            set_last_updated_text(`Last update:
                ${formatDistanceToNowStrict(last_updated, {
                    unit: 'second',
                    addSuffix: true,
                })}`);
        },
        // Delay in milliseconds or null to stop it
        1_000
    );
    useInterval(
        () => {
            // eslint-disable-next-line no-console
            console.log('refrescando');
            set_jwt(obtenerJWT());
            set_last_updated(new Date());
        },
        // Delay in milliseconds or null to stop it
        30_000
    );
    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log('refrescando');
        set_jwt(obtenerJWT());
        set_last_updated(new Date());
    }, []);

    const columns: DataTableColumn<TimingInfo>[] = useMemo(() => {
        return [
            {
                accessor: 'name',
            },
            {
                accessor: 'timestamp',
            },
            {
                accessor: 'date',
            },
            {
                accessor: 'diff',
            },
        ] as DataTableColumn<TimingInfo>[];
    }, []);
    const records: TimingInfo[] = useMemo(() => {
        if (!jwt) return [];
        const salida: TimingInfo[] = [];
        const iat = jwt.iat;
        if (iat) {
            salida.push(formatTimestamp('issued at (iat)', iat));
        }
        const exp = jwt.exp;
        if (exp) {
            salida.push(formatTimestamp('expire (exp)', exp));
        }
        return salida;
    }, [jwt]);
    return (
        <Panel
            titulo={'JWT'}
            extras={
                <Button onClick={jwtDisclosure.toggle}>
                    {jwtAbierto ? 'Ocultar' : 'Mostrar'}
                </Button>
            }
        >
            {jwt ? (
                <Collapse in={jwtAbierto}>
                    <strong>sFrontToken:</strong> <br />
                    <JsonViewer value={jwt} />
                    <DataTable
                        columns={columns}
                        records={records}
                    />
                    <Button
                        onClick={async () => {
                            await todayQuery();
                            set_jwt(obtenerJWT());
                            set_last_updated(new Date());
                        }}
                    >
                        Test
                    </Button>
                </Collapse>
            ) : null}
            <small>{last_updated_text}</small>
            {data ? <JsonViewer value={data} /> : null}
            {error ? <JsonViewer value={error} /> : null}
        </Panel>
    );
}

function formatTimestamp(name: string, time: number): TimingInfo {
    const date = fromUnixTime(time);
    return {
        id: name,
        name,
        timestamp: time,
        date: formatDateTime(date),
        diff: formatDistanceToNow(date, {
            includeSeconds: true,
            addSuffix: true,
        }),
    };
}

function obtenerJWT(): JWTFields | undefined {
    try {
        const cookies = cookie.parse(document.cookie);
        const parsed = JSON.parse(window.atob(cookies.sFrontToken)) as {
            up?: JWTFields;
        };
        return parsed?.up;
    } catch (error) {
        console.error(error);
        return undefined;
    }
}
